<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import AiAgentBadge from './AiAgentBadge.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import ConversationHeaderParticipants from './ConversationHeaderParticipants.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { evaluateSLAStatus } from '@chatwoot/utils';
import { useMessagingWindow } from 'dashboard/composables/useMessagingWindow';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFocusConversationQueue } from 'dashboard/composables/useFocusConversationQueue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

// SiteUp — Modo Foco: elementos do cabeçalho que o usuário escolheu ocultar
// (ui_settings.conversation_header_hidden_items).
const { uiSettings: headerUiSettings } = useUISettings();
const { isListHidden, currentWaitingMinutes, finalizeCurrent } =
  useFocusConversationQueue();

const waitingLabel = computed(() => {
  const minutes = currentWaitingMinutes.value;
  if (minutes < 1) return t('CHAT_LIST.FOCUS_QUEUE.WAITING_NOW');
  if (minutes >= 60) {
    return t('CHAT_LIST.FOCUS_QUEUE.WAITING_HOURS', {
      n: Math.floor(minutes / 60),
    });
  }
  return t('CHAT_LIST.FOCUS_QUEUE.WAITING', { n: minutes });
});
const hiddenHeaderItems = computed(() => {
  const hidden = headerUiSettings.value?.conversation_header_hidden_items;
  return Array.isArray(hidden) ? hidden : [];
});
const isHeaderItemHidden = name => hiddenHeaderItems.value.includes(name);
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

// SiteUp: compressão por largura do header (painel estreito), não só viewport.
// Meta (inbox/assignee/CSW) compacta cedo; labels do pill IA/Pausar só em painel bem estreito
// (<560), senão o split-pane local degrada o mock C (só dot + pause + Resolve).
const isCompactHeader = computed(() => width.value > 0 && width.value < 720);
const isActionsCompact = computed(() => width.value > 0 && width.value < 560);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(
      snoozedUntil
    )}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

// SiteUp: agente atribuído mostrado no header (antes só aparecia no painel
// lateral "Ações da conversa"). Reflete o assignee em tempo real — inclusive
// quando o auto-assign-on-reply atribui ao responder. Participantes extras +
// leave (self) ficam em ConversationHeaderParticipants.
const assignedAgent = computed(() => currentChat.value?.meta?.assignee);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// SiteUp: faixa de urgência no header (prioridade + SLA), mesma lógica do card.
// Vermelha = urgente / SLA estourado; âmbar = alta / SLA perto de estourar.
const urgencyStripeClass = computed(() => {
  const priority = props.chat?.priority;
  let sla = null;
  const appliedSla = props.chat?.applied_sla;
  if (appliedSla?.id) {
    const status = evaluateSLAStatus({ appliedSla, chat: props.chat });
    if (status?.isSlaMissed) sla = 'high';
    else if (status?.threshold) sla = 'medium';
  }
  if (priority === 'urgent' || sla === 'high') return 'bg-n-ruby-9';
  if (priority === 'high' || sla === 'medium') return 'bg-n-amber-9';
  return '';
});

// SiteUp: CSW timer + optional FEP (free templates) badge in the header.
const {
  hasWindow: hasMessagingWindow,
  label: messagingWindowLabel,
  icon: messagingWindowIcon,
  tooltip: messagingWindowTooltip,
  isUrgent: messagingWindowUrgent,
  level: messagingWindowLevel,
  fepActive: messagingWindowFepActive,
  fepShortLabel: messagingWindowFepShort,
} = useMessagingWindow(() => currentChat.value);

// SiteUp V2 (G4): .hpill — fundo elev neutro; cor semântica só no dot (7px).
const messagingWindowDotClass = computed(() => {
  switch (messagingWindowLevel.value) {
    case 'expired':
    case 'urgent':
      return 'bg-n-ruby-9';
    case 'warning':
      return 'bg-n-amber-9';
    case 'ok':
      return 'bg-n-teal-9';
    default:
      return 'bg-n-slate-10';
  }
});

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    data-testid="focus-spot-header"
    class="relative z-10 flex flex-row flex-nowrap items-center justify-between gap-2 shrink-0 w-full min-w-0 px-4 py-2.5 min-h-14 overflow-visible bg-n-surface-1 border-b border-n-weak"
  >
    <!-- SiteUp: faixa de urgência (prioridade + SLA) -->
    <span
      v-if="urgencyStripeClass"
      class="absolute inset-y-0 ltr:left-0 rtl:right-0 w-1"
      :class="urgencyStripeClass"
      aria-hidden="true"
    />
    <div
      class="flex items-center justify-start min-w-0 flex-1 overflow-x-hidden gap-3"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="shrink-0"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="40"
        :status="currentContact.availability_status"
        hide-offline-status
        class="shrink-0"
      />
      <div
        class="flex flex-col justify-center items-start min-w-0 overflow-x-hidden"
      >
        <div
          class="flex flex-row items-center max-w-full gap-1 p-0 m-0 min-w-0 leading-none"
        >
          <!-- SiteUp V2 (G4): nome Display 700 15px (font-bold → Madefor Display) -->
          <span
            class="text-[15px] font-bold tracking-tight truncate leading-5 text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <span
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            class="i-ri-alert-line size-3.5 text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
          />
        </div>

        <!-- SiteUp V2 (G4): subtítulo muted 12.5px -->
        <div
          class="flex items-center gap-1 overflow-hidden text-[12.5px] leading-4 conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap min-w-0 max-w-full"
        >
          <button
            type="button"
            class="truncate text-[12.5px] leading-4 text-n-slate-11 hover:text-n-slate-12 !p-0 cursor-pointer shrink-0"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <template
            v-if="(hasMultipleInboxes || isListHidden) && !isCompactHeader"
          >
            <span class="text-n-slate-10">·</span>
            <InboxName :inbox="inbox" class="!mx-0 !text-[12.5px]" />
          </template>
          <template v-if="isSnoozed">
            <span class="text-n-slate-10">·</span>
            <span class="font-medium text-n-amber-10 truncate">
              {{ snoozedDisplayText }}
            </span>
          </template>
          <!-- SiteUp: assignee + participantes; leave (self) no hover X -->
          <ConversationHeaderParticipants
            :conversation-id="chat.id"
            :assignee="assignedAgent"
            :compact="isCompactHeader"
          />
        </div>
      </div>
    </div>
    <div
      class="flex flex-row flex-nowrap items-center justify-end shrink-0 gap-1.5 sm:gap-2 header-actions-wrap"
    >
      <!-- SiteUp V2 (G4): pílulas de estado .hpill (janela + FEP) — cor semântica só no dot/ícone -->
      <template v-if="hasMessagingWindow && !isCompactHeader">
        <span
          v-tooltip.bottom="messagingWindowTooltip"
          class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-n-alpha-2 text-[12.5px] font-bold leading-none text-n-slate-12 shrink-0"
          :class="messagingWindowUrgent && 'animate-pulse'"
        >
          <span
            class="size-[7px] rounded-full shrink-0"
            :class="messagingWindowDotClass"
            aria-hidden="true"
          />
          <span
            :class="messagingWindowIcon"
            class="size-3.5 shrink-0 text-n-slate-11"
          />
          {{ messagingWindowLabel }}
        </span>
      </template>
      <template v-if="messagingWindowFepActive && !isCompactHeader">
        <span
          class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-n-alpha-2 text-[12.5px] font-bold leading-none text-n-slate-12 shrink-0"
        >
          <span
            class="size-[7px] rounded-full shrink-0 bg-n-teal-9"
            aria-hidden="true"
          />
          <span
            class="i-ri-discount-percent-line size-3.5 shrink-0 text-n-slate-11"
          />
          {{ messagingWindowFepShort }}
        </span>
      </template>
      <span
        v-if="isListHidden"
        class="shrink-0 text-[12.5px] font-bold text-n-amber-10 tabular-nums"
        data-testid="focus-queue-waiting"
      >
        {{ waitingLabel }}
      </span>
      <button
        v-if="isListHidden"
        type="button"
        class="inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
        data-testid="focus-queue-finish"
        @click="finalizeCurrent"
      >
        <span class="i-ri-check-line size-3.5" aria-hidden="true" />
        {{ t('CHAT_LIST.FOCUS_QUEUE.FINISH') }}
      </button>
      <!-- SiteUp: IA status (.hpill) + Pausar + Resolver branco pílula -->
      <AiAgentBadge v-if="!isListHidden" :compact="isActionsCompact">
        <template #trailing>
          <ResolveAction grouped />
        </template>
      </AiAgentBadge>
      <SLACardLabel
        v-if="hasSlaPolicyId && !isCompactHeader && !isHeaderItemHidden('sla')"
        :chat="chat"
        show-extended-info
        :parent-width="width"
      />
      <ConversationCallButton
        v-if="!isHeaderItemHidden('call_button')"
        :inbox="inbox"
        :chat="currentChat"
      />
      <MoreActions :show-resolve="false" />
    </div>
  </div>
</template>
